"use client";

import * as React from "react";
import { CheckIcon, ChevronDownIcon, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/shadcn/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/shadcn/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shadcn/popover";
import { Badge, type BadgeProps } from "@/components/shadcn/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import { Separator } from "@/components/shadcn/separator";

export type ComboboxOption = {
  label: string;
  value: string;
  icon?: string | React.ReactNode;
  description?: string;
  badge?: string;
  badgeVariant?: BadgeProps["variant"];
  bigIcon?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
  /** Render a horizontal separator below this option. */
  separator?: boolean;
  /**
   * Hide this option from the list as soon as the user types anything into
   * the search input. Useful for "Personal workspace"-style options that
   * shouldn't compete with name-based matches when searching.
   */
  hideOnSearch?: boolean;
  custom?: Record<string, unknown>;
};

interface ComboboxProps {
  options: ComboboxOption[];
  value?: string;
  placeholder?: string;
  emptyText?: string;
  searchPlaceholder?: string;
  onValueChange?: (value: string | undefined) => void;
  disabled?: boolean;
  loading?: boolean;
  clearable?: boolean;
  searchable?: boolean;
  className?: string;
  // DOM id forwarded to the trigger button. Wire this to a sibling
  // <label htmlFor=...> for keyboard/screen-reader association.
  id?: string;
  // Accessible name for the trigger, for repeated-row editors where a visible
  // per-row label would be noise but the control still needs a name.
  ariaLabel?: string;
  multiple?: false;
  // Render the popover into a body-level portal so it isn't clipped by an
  // ancestor's `overflow: hidden` (modals, scrollable forms). Defaults to
  // false to preserve existing in-place rendering callers depend on.
  hasPortal?: boolean;
  // Server-driven search: when set, the parent owns filtering, so cmdk's own
  // substring filter is disabled — otherwise it would hide server matches
  // that don't contain the raw query.
  onSearchChange?: (search: string) => void;
  // Called when the end of the list scrolls into view, for paged options.
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  // Always rendered on the trigger, unlike the per-option icon.
  triggerIcon?: React.ReactNode;
  customItemContent?: (
    option?: ComboboxOption,
    selected?: boolean
  ) => React.ReactNode;
}

interface MultiComboboxProps {
  options: ComboboxOption[];
  value?: string[];
  placeholder?: string;
  emptyText?: string;
  searchPlaceholder?: string;
  onValueChange?: (value: string[]) => void;
  disabled?: boolean;
  loading?: boolean;
  clearable?: boolean;
  searchable?: boolean;
  className?: string;
  multiple: true;
  maxOptions?: number;
  selectAll?: boolean;
  customItemContent?: (
    option?: ComboboxOption,
    selected?: boolean
  ) => React.ReactNode;
}

export function Combobox({
  options,
  value,
  placeholder = "Select option...",
  emptyText = "No options found.",
  searchPlaceholder = "Search options...",
  onValueChange,
  disabled = false,
  loading = false,
  clearable = false,
  searchable = true,
  className,
  hasPortal = false,
  id,
  ariaLabel,
  onSearchChange,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  triggerIcon,
  customItemContent,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const selectedOption = options.find((option) => option.value === value);

  const observer = React.useRef<IntersectionObserver>(undefined);
  const onLoadMoreRef = React.useRef(onLoadMore);
  React.useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  }, [onLoadMore]);
  React.useEffect(() => () => observer.current?.disconnect(), []);

  const loadMoreRef = React.useCallback((node: HTMLDivElement | null) => {
    observer.current?.disconnect();
    if (!node) return;
    observer.current = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) onLoadMoreRef.current?.();
    });
    observer.current.observe(node);
  }, []);

  // Strip hideOnSearch options as soon as the user starts typing. cmdk's
  // own substring filter can't express "hide when search is non-empty"
  // because it operates per-option without seeing the global predicate.
  const visibleOptions = React.useMemo(() => {
    if (!search) return options;
    return options.filter((option) => !option.hideOnSearch);
  }, [options, search]);

  const handleSelect = (selectedValue: string) => {
    const newValue = selectedValue === value ? undefined : selectedValue;
    if (!clearable && newValue === undefined) {
      return;
    }
    onValueChange?.(newValue);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onValueChange?.(undefined);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          disabled={disabled || loading}
          className={cn(
            "w-full justify-between hover:bg-background aria-expanded:bg-background focus-within:ring-ring focus-within:ring-1",
            className,
            open && "ring-ring! ring-1!"
          )}
        >
          {triggerIcon && <span className="flex-shrink-0">{triggerIcon}</span>}
          {customItemContent ? (
            selectedOption ? (
              customItemContent(selectedOption)
            ) : (
              <span className="truncate text-sm leading-normal font-normal text-muted-foreground">
                {placeholder}
              </span>
            )
          ) : (
            <div className="flex items-center gap-2 flex-1 text-left overflow-hidden">
              {selectedOption?.icon &&
                typeof selectedOption.icon === "string" && (
                  <img
                    src={selectedOption.icon}
                    alt={selectedOption.label}
                    className="w-4 h-4 flex-shrink-0"
                  />
                )}
              {selectedOption?.icon &&
                typeof selectedOption.icon !== "string" &&
                selectedOption.icon}
              <span
                className={cn(
                  "truncate text-sm leading-normal font-normal",
                  selectedOption ? "text-foreground" : "text-muted-foreground"
                )}
              >
                {selectedOption?.label || placeholder}
              </span>
              {selectedOption?.badge && (
                <Badge
                  variant={selectedOption.badgeVariant ?? "secondary"}
                  isCompact
                >
                  {selectedOption.badge}
                </Badge>
              )}
            </div>
          )}
          <div className="flex items-center self-end gap-1">
            {clearable && value && !disabled && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClear}
                className="h-4 w-4 opacity-50 hover:opacity-100 bg-transparent hover:bg-transparent"
              >
                <X size={12} color="var(--foreground)" />
              </Button>
            )}
            <ChevronDownIcon className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-full max-w-[400px] p-0 z-5000"
        align="start"
        hasPortal={hasPortal}
      >
        <Command shouldFilter={!onSearchChange}>
          {searchable && (
            <CommandInput
              placeholder={searchPlaceholder}
              value={search}
              onValueChange={(nextSearch) => {
                setSearch(nextSearch);
                onSearchChange?.(nextSearch);
              }}
            />
          )}
          <CommandList>
            <CommandEmpty>
              {isLoadingMore ? "Loading..." : emptyText}
            </CommandEmpty>
            <CommandGroup>
              {visibleOptions.map((option, index) => {
                const item = (
                  <CommandItem
                    key={option.value}
                    value={`${option.label}-${index}`.trim()}
                    disabled={option.disabled}
                    onSelect={() =>
                      !option.disabled && handleSelect(option.value)
                    }
                    className={cn(
                      option.disabled && "opacity-50 cursor-not-allowed",
                      "cursor-pointer"
                    )}
                  >
                    {customItemContent ? (
                      customItemContent(option, value === option.value)
                    ) : (
                      <div className="flex items-center gap-2 w-full min-w-0">
                        {option.icon && typeof option.icon === "string" && (
                          <img
                            src={option.icon}
                            alt={option.label}
                            className="w-6 h-6 flex-shrink-0"
                          />
                        )}
                        {option.icon &&
                          typeof option.icon !== "string" &&
                          option.icon}
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="flex items-center gap-2 min-w-0">
                            <span className="truncate">{option.label}</span>
                            {option.badge && (
                              <Badge
                                variant={option.badgeVariant ?? "secondary"}
                                isCompact
                              >
                                {option.badge}
                              </Badge>
                            )}
                          </span>
                          {option.description && (
                            <span className="text-sm text-muted-foreground truncate">
                              {option.description}
                            </span>
                          )}
                        </div>
                        <CheckIcon
                          className={cn(
                            "h-4 w-4 flex-shrink-0 ml-auto",
                            value === option.value ? "opacity-100" : "opacity-0"
                          )}
                        />
                      </div>
                    )}
                  </CommandItem>
                );

                const wrapped =
                  option.disabled && option.disabledTooltip ? (
                    <Tooltip key={option.value}>
                      <TooltipTrigger asChild>
                        <div>{item}</div>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs">
                        {option.disabledTooltip}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    item
                  );

                return (
                  <React.Fragment key={option.value}>
                    {wrapped}
                    {option.separator && <Separator className="my-1" />}
                  </React.Fragment>
                );
              })}
            </CommandGroup>
            {onLoadMore && hasMore && !isLoadingMore && (
              <div ref={loadMoreRef} className="h-px" />
            )}
            {isLoadingMore && visibleOptions.length > 0 && (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                Loading...
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function MultiCombobox({
  options,
  value = [],
  placeholder = "Select options...",
  emptyText = "No options found.",
  searchPlaceholder = "Search options...",
  onValueChange,
  disabled = false,
  loading = false,
  clearable = false,
  searchable = true,
  className,
  maxOptions,
  selectAll = false,
  customItemContent,
}: MultiComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const selectedOptions = options.filter((option) =>
    value.includes(option.value)
  );

  // Strip hideOnSearch options once the user types — matches the single
  // Combobox behaviour. cmdk's own substring filter can't express "hide
  // when search is non-empty" because it runs per-option.
  const visibleOptions = React.useMemo(() => {
    if (!search) return options;
    return options.filter((option) => !option.hideOnSearch);
  }, [options, search]);

  const handleSelect = (selectedValue: string) => {
    const newValue = value.includes(selectedValue)
      ? value.filter((v) => v !== selectedValue)
      : [...value, selectedValue];
    onValueChange?.(newValue);
  };

  const handleClear = (e: React.MouseEvent) => {
    onValueChange?.([]);
  };

  const selectableValues = options
    .filter((option) => !option.disabled)
    .map((option) => option.value);
  const allSelected =
    selectableValues.length > 0 &&
    selectableValues.every((v) => value.includes(v));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || loading}
          className={cn(
            "w-full justify-between min-h-[40px] h-auto",
            className
          )}
        >
          <div className="flex items-center gap-1 flex-1 text-left overflow-hidden flex-wrap">
            {selectedOptions.length > 0 ? (
              selectedOptions.map((option) => (
                <Badge
                  key={option.value}
                  variant="secondary"
                  className="flex items-center gap-1 text-xs"
                >
                  {option.icon && typeof option.icon === "string" && (
                    <img
                      src={option.icon}
                      alt={option.label}
                      className="w-3 h-3 flex-shrink-0"
                    />
                  )}
                  {option.icon &&
                    typeof option.icon !== "string" &&
                    option.icon}
                  <span className="truncate">{option.label}</span>
                </Badge>
              ))
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {clearable && value.length > 0 && !disabled && (
              <X
                className="h-4 w-4 opacity-50 hover:opacity-100"
                onClick={handleClear}
              />
            )}
            <ChevronDownIcon className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-full max-w-[400px] p-0 z-5000"
        align="start"
        hasPortal={false}
      >
        <Command>
          {searchable && (
            <CommandInput
              placeholder={searchPlaceholder}
              value={search}
              onValueChange={setSearch}
            />
          )}
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {selectAll && !search && selectableValues.length > 0 && (
                <CommandItem
                  value="__select_all__"
                  onSelect={() =>
                    onValueChange?.(allSelected ? [] : selectableValues)
                  }
                >
                  <div className="flex items-center gap-2 w-full min-w-0">
                    <CheckIcon
                      className={cn(
                        "h-4 w-4 flex-shrink-0",
                        allSelected ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="truncate">Select all</span>
                  </div>
                </CommandItem>
              )}
              {visibleOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={`${option.label} ${option.description ?? ""}`.trim()}
                  disabled={
                    option.disabled ||
                    (maxOptions
                      ? value.length >= maxOptions &&
                        !value.includes(option.value)
                      : false)
                  }
                  onSelect={() =>
                    !option.disabled &&
                    !(
                      maxOptions &&
                      value.length >= maxOptions &&
                      !value.includes(option.value)
                    ) &&
                    handleSelect(option.value)
                  }
                  className={cn(
                    option.disabled && "opacity-50 cursor-not-allowed"
                  )}
                >
                  {customItemContent ? (
                    customItemContent(option, value.includes(option.value))
                  ) : (
                    <div className="flex items-center gap-2 w-full min-w-0">
                      <CheckIcon
                        className={cn(
                          "h-4 w-4 flex-shrink-0",
                          value.includes(option.value)
                            ? "opacity-100"
                            : "opacity-0"
                        )}
                      />
                      {option.icon && typeof option.icon === "string" && (
                        <img
                          src={option.icon}
                          alt={option.label}
                          className="w-6 h-6 flex-shrink-0"
                        />
                      )}
                      {option.icon &&
                        typeof option.icon !== "string" &&
                        option.icon}
                      <div className="flex flex-col flex-1 min-w-0">
                        <span className="flex items-center gap-2 min-w-0">
                          <span className="truncate">{option.label}</span>
                          {option.badge && (
                            <Badge
                              variant={option.badgeVariant ?? "secondary"}
                              isCompact
                            >
                              {option.badge}
                            </Badge>
                          )}
                        </span>
                        {option.description && (
                          <span className="text-sm text-muted-foreground truncate">
                            {option.description}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ExampleCombobox() {
  const [value, setValue] = React.useState<string>();

  const frameworks = [
    { value: "next.js", label: "Next.js" },
    { value: "sveltekit", label: "SvelteKit" },
    { value: "nuxt.js", label: "Nuxt.js" },
    { value: "remix", label: "Remix" },
    { value: "astro", label: "Astro" },
  ];

  return (
    <Combobox
      options={frameworks}
      value={value}
      onValueChange={setValue}
      placeholder="Select framework..."
      clearable
    />
  );
}
